import { Alert, Input, Layouts, Loader, UploadImages } from 'components'
import s from './styles.module.scss'
import DashBoardNav from '../../dashboardNav'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from 'api'
import { head, imageCompressor } from 'helpers'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

export default function Main() {
  const [loading, setLoading] = useState(false)
  const [partners, setPartners] = useState([])
  const [newPartners, setNewPartners] = useState([])
  const [partnersImages, setPartnersImages] = useState([])
  const [partnersImagesUrls, setPartnersImagesUrls] = useState([])
  const [newPartnersImages, setNewPartnersImages] = useState([])
  const [newPartnersImagesUrls, setNewPartnersImagesUrls] = useState([])
  const [partnersToDelete, setPartnersToDelete] = useState([])
  const navigate = useNavigate()
  const processing = useRef(false)

  const getPartners = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    try {
      const res = await api.admin.partner.fetch({})
      
      console.log('📊 Fetch partners response:', res)
      
      if (res?.code === 200) {
        const fetchedPartners = res.payload?.partners || []
        console.log('✅ Fetched partners:', fetchedPartners.length, fetchedPartners)
        setPartners(fetchedPartners)
        setPartnersImages(Array(fetchedPartners.length).fill(null))
        // Handle both full URLs and filenames
        setPartnersImagesUrls(fetchedPartners.map(p => {
          if (!p.logo) return null
          return IMAGE_HOST + p.logo
        }))
      } else {
        console.error('❌ Failed to fetch partners:', res)
        Alert.error(res?.message || 'Failed to fetch partners')
      }
    } catch (error) {
      console.error('Error fetching partners:', error)
      Alert.error('Failed to load partners')
    }

    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    getPartners()
  }, [getPartners])

  useEffect(() => {
    head({ title: 'Partners | BCN' })
  }, [])

  // Handlers for existing partners
  const handlePartnerChange = (index, field, value) => {
    const updated = [...partners]
    updated[index] = { ...updated[index], [field]: value }
    setPartners(updated)
  }

  const handlePartnerImageChange = async (e, index) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const compressedFiles = await imageCompressor([file], 'partner')
      if (compressedFiles[0]) {
        const imageUrl = URL.createObjectURL(compressedFiles[0])
        const updatedImages = [...partnersImages]
        const updatedUrls = [...partnersImagesUrls]
        updatedImages[index] = compressedFiles[0]
        updatedUrls[index] = imageUrl
        setPartnersImages(updatedImages)
        setPartnersImagesUrls(updatedUrls)
      }
    } catch (error) {
      console.error('Error compressing image:', error)
      Alert.error('Failed to process image')
    }
  }

  const handleRemovePartner = (index) => {
    const partner = partners[index]
    if (partner._id) {
      setPartnersToDelete([...partnersToDelete, partner._id])
    }
    
    const updated = partners.filter((_, i) => i !== index)
    const updatedImages = partnersImages.filter((_, i) => i !== index)
    const updatedUrls = partnersImagesUrls.filter((_, i) => i !== index)
    
    setPartners(updated)
    setPartnersImages(updatedImages)
    setPartnersImagesUrls(updatedUrls)
  }

  // Handlers for new partners
  const handleNewPartnerChange = (index, field, value) => {
    const updated = [...newPartners]
    updated[index] = { ...updated[index], [field]: value }
    setNewPartners(updated)
  }

  const handleNewPartnerImageChange = async (e, index) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const compressedFiles = await imageCompressor([file], 'partner')
      if (compressedFiles[0]) {
        const imageUrl = URL.createObjectURL(compressedFiles[0])
        const updatedImages = [...newPartnersImages]
        const updatedUrls = [...newPartnersImagesUrls]
        updatedImages[index] = compressedFiles[0]
        updatedUrls[index] = imageUrl
        setNewPartnersImages(updatedImages)
        setNewPartnersImagesUrls(updatedUrls)
      }
    } catch (error) {
      console.error('Error compressing image:', error)
      Alert.error('Failed to process image')
    }
  }

  const handleRemoveNewPartner = (index) => {
    const updated = newPartners.filter((_, i) => i !== index)
    const updatedImages = newPartnersImages.filter((_, i) => i !== index)
    const updatedUrls = newPartnersImagesUrls.filter((_, i) => i !== index)
    
    setNewPartners(updated)
    setNewPartnersImages(updatedImages)
    setNewPartnersImagesUrls(updatedUrls)
  }

  const addNewPartner = () => {
    setNewPartners([...newPartners, { name: '', url: '', subCategory: '', order: partners.length + newPartners.length }])
    setNewPartnersImages([...newPartnersImages, null])
    setNewPartnersImagesUrls([...newPartnersImagesUrls, null])
  }

  const saveHandler = async () => {
    // Validate existing partners
    for (const partner of partners) {
      if (!partner.name?.trim()) {
        Alert.warn('Please fill partner name for all existing partners')
        return
      }
    }

    // Validate new partners
    for (const partner of newPartners) {
      if (!partner.name?.trim()) {
        Alert.warn('Please fill partner name for all new partners')
        return
      }
    }

    if (processing.current) return
    processing.current = true
    Alert.success('Saving partners...')
    setLoading(true)

    try {
      // Delete removed partners
      for (const id of partnersToDelete) {
        await api.admin.partner.delete(id)
      }

      // Update existing partners
      for (let i = 0; i < partners.length; i++) {
        const partner = partners[i]
        const data = new FormData()
        data.append('name', partner.name)
        if (partner.url) {
          data.append('url', partner.url)
        }
        if (partner.subCategory) {
          data.append('subCategory', partner.subCategory)
        }
        if (partner.order !== undefined) {
          data.append('order', partner.order)
        }
        
        if (partnersImages[i]) {
          data.append('logo', partnersImages[i])
        }

        await api.admin.partner.update(partner._id, data)
      }

      // Create new partners
      for (let i = 0; i < newPartners.length; i++) {
        const partner = newPartners[i]
        const data = new FormData()
        data.append('name', partner.name)
        if (partner.url && partner.url.trim()) {
          data.append('url', partner.url.trim())
        }
        if (partner.subCategory && partner.subCategory.trim()) {
          data.append('subCategory', partner.subCategory.trim())
        }
        if (partner.order !== undefined) {
          data.append('order', partner.order)
        }
        
        if (newPartnersImages[i]) {
          data.append('logo', newPartnersImages[i])
        }

          const createRes = await api.admin.partner.create(data)
        console.log('📝 Create partner response:', createRes)
        
        if (createRes?.code !== 201 && createRes?.code !== 200) {
          console.error('❌ Failed to create partner:', createRes)
          Alert.error(`Failed to create partner "${partner.name}": ${createRes?.message || 'Unknown error'}`)
        } else {
          console.log('✅ Partner created successfully:', partner.name)
        }
      }

      Alert.success('Partners saved successfully')
      
      // Clear new partners first
      setNewPartners([])
      setNewPartnersImages([])
      setNewPartnersImagesUrls([])
      setPartnersToDelete([])
      
      // Small delay to ensure backend has processed
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Refresh the list
      await getPartners()
    } catch (error) {
      console.error('Error saving partners:', error)
      Alert.error('Failed to save partners')
    }

    setLoading(false)
    processing.current = false
  }

  return (
    <div className={s.main}>
      <div className={s.partnersOuter}>
        <Layouts.Classic title='Partners'>
          <div className={s.headerBottom}>
            <div className={s.addButton} onClick={saveHandler}>
              <span className='material-icons-outlined'>save</span> Save
            </div>
          </div>
        </Layouts.Classic>

        {!loading ? (
          <div className={s.content + ' innerScrollX'}>
            <DashBoardNav />
            
            <div className={s.partners}>
              <div className={s.label}>Partners {partners.length > 0 && `(${partners.length})`}</div>
              
              {partners.length === 0 && newPartners.length === 0 && (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                  No partners yet. Click "Add Partner" to create one.
                </div>
              )}
              
              {/* Existing Partners */}
              {partners.map((partner, i) => (
                <PartnerForm
                  key={`existing-${partner._id || i}`}
                  index={i}
                  partner={partner}
                  imageUrl={partnersImagesUrls[i]}
                  onChange={handlePartnerChange}
                  onImageChange={handlePartnerImageChange}
                  onRemove={handleRemovePartner}
                  isNew={false}
                />
              ))}

              {/* New Partners */}
              {newPartners.map((partner, i) => (
                <PartnerForm
                  key={`new-${i}`}
                  index={i}
                  displayIndex={partners.length + i}
                  partner={partner}
                  imageUrl={newPartnersImagesUrls[i]}
                  onChange={handleNewPartnerChange}
                  onImageChange={handleNewPartnerImageChange}
                  onRemove={handleRemoveNewPartner}
                  isNew={true}
                />
              ))}
            </div>

            <div className={s.addMore}>
              <div onClick={addNewPartner}>
                Add Partner <span className='material-icons-outlined'>add_circle_outline</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={s.loader}>
            <Loader message='Loading partners...' color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        )}
      </div>
    </div>
  )
}

const PartnerForm = ({ index, displayIndex, partner, imageUrl, onChange, onImageChange, onRemove, isNew }) => {
  const actualIndex = displayIndex !== undefined ? displayIndex : index
  
  return (
    <div className={s.partner}>
      <div className={s.title}>
        <span>Partner {actualIndex + 1}</span>
        <span onClick={() => onRemove(index)}>
          Remove <span className='material-icons-outlined'>cancel</span>
        </span>
      </div>

      <div className={s.imageUpload}>
        <UploadImages.Single
          userLogoUrl={imageUrl || ''}
          handleUserLogoChange={(e) => onImageChange(e, index)}
          id={`partnerImage-${isNew ? 'new' : 'existing'}-${index}`}
        />
      </div>

      <Input.Classic
        type='text'
        label='Partner Name'
        iconLeft='business'
        placeholder='Enter partner name'
        value={partner.name || ''}
        onChange={(e) => onChange(index, 'name', e.target.value)}
      />

      <Input.Classic
        type='url'
        label='Website URL (Optional)'
        iconLeft='link'
        placeholder='https://example.com'
        value={partner.url || ''}
        onChange={(e) => onChange(index, 'url', e.target.value)}
      />

      <Input.Classic
        type='text'
        label='Sub Category (Optional)'
        iconLeft='category'
        placeholder='e.g., Technology, Finance, Healthcare'
        value={partner.subCategory || ''}
        onChange={(e) => onChange(index, 'subCategory', e.target.value)}
      />

      <Input.Classic
        type='number'
        label='Display Order'
        iconLeft='sort'
        placeholder='0'
        value={partner.order !== undefined ? partner.order : ''}
        onChange={(e) => onChange(index, 'order', parseInt(e.target.value) || 0)}
      />
    </div>
  )
}

